import React, { useState } from "react";
import API from "../../api/Createapi";
import "./AuthModal.css";
import {
    FaUser, FaEnvelope, FaLock, FaPhone,
    FaCity, FaMapMarkerAlt, FaHashtag,
    FaEye, FaEyeSlash, FaUserPlus,
    FaTimes, FaBolt, FaShieldAlt, FaHeadset,
    FaArrowLeft
} from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Loader from "../Loader";

/*
  RegisterModal — admin-triggered popup
  Props:
    onClose()  — closes the modal
*/
function RegisterModal({ onClose }) {
    const [loading, setLoading] = useState(false);
    const [form, setForm] = useState({
        fullname: "", username: "", email: "",
        password: "", confirmPassword: "",
        mobile: "", city: "", address: "", pincode: ""
    });
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [agreeTerms, setAgreeTerms] = useState(false);

    const handleChange = (e) =>
        setForm({ ...form, [e.target.name]: e.target.value });

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (form.password !== form.confirmPassword) {
            toast.error("Passwords do not match!");
            return;
        }
        if (form.password.length < 6) {
            toast.error("Password must be at least 6 characters.");
            return;
        }

        setLoading(true);
        try {
            const res = await API.post("/register.php", form);
            if (res.data.status === "success") {
                toast.success("Account registered successfully!");
                setForm({
                    fullname: "", username: "", email: "",
                    password: "", confirmPassword: "",
                    mobile: "", city: "", address: "", pincode: ""
                });
                setTimeout(() => onClose(true), 1500);
            } else if (res.data.status === "exists") {
                toast.error("Username or email or Mobile Number already registered!");
            } else {
                toast.error(res.data.message || "Registration failed.");
            }
        } catch (err) {
            console.error(err);
            toast.error("Server connection error.");
        } finally {
            setLoading(false);
        }
    };

    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget) onClose();
    };

    return (
        <div className="register-modal-overlay" onClick={handleOverlayClick}>
            {loading && <Loader />}

            <div className="register-modal-card">
                {/* ── LEFT HERO PANEL ── */}
                <div className="auth-modal-hero" style={{ flex: "0.75" }}>
                    <button className="hero-back-btn" onClick={onClose} type="button" title="Back to dashboard">
                        <FaArrowLeft /> Back
                    </button>
                    <div className="auth-modal-glow auth-modal-glow-1" />
                    <div className="auth-modal-glow auth-modal-glow-2" />
                    <div className="auth-modal-hero-content">
                        <div className="auth-modal-brand">
                            RiTU <span>BROAD BAND</span>
                        </div>
                        <h2 className="auth-modal-hero-title">Register New Account</h2>
                        <p className="auth-modal-hero-desc">
                            Create a new customer account with full access to fiber plans, payments, and account management.
                        </p>
                        <div className="auth-modal-features">
                            <div className="auth-modal-feature-item">
                                <div className="auth-modal-feature-icon"><FaBolt /></div>
                                <span>Instant Account Activation</span>
                            </div>
                            <div className="auth-modal-feature-item">
                                <div className="auth-modal-feature-icon"><FaShieldAlt /></div>
                                <span>Secure Data Handling</span>
                            </div>
                            <div className="auth-modal-feature-item">
                                <div className="auth-modal-feature-icon"><FaHeadset /></div>
                                <span>24/7 Customer Support</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* ── RIGHT FORM PANEL ── */}
                <div className="auth-modal-form-panel" style={{ flex: "1.25", padding: "26px 28px", overflowY: "auto" }}>
                    <div className="auth-modal-header">
                        <h2>Create Account</h2>
                        <p>Fill in all the details to register a new customer</p>
                    </div>

                    <form className="auth-modal-form" onSubmit={handleSubmit}>
                        {/* Full Name & Username */}
                        <div className="auth-modal-grid-row">
                            <div className="auth-modal-input-group">
                                <label htmlFor="reg-fullname">Full Name *</label>
                                <div className="auth-modal-input-wrap">
                                    <span className="auth-modal-input-icon"><FaUser /></span>
                                    <input
                                        id="reg-fullname" name="fullname" type="text"
                                        placeholder="Full Name"
                                        value={form.fullname} onChange={handleChange}
                                        required style={{ textTransform: "capitalize" }}
                                    />
                                </div>
                            </div>
                            <div className="auth-modal-input-group">
                                <label htmlFor="reg-username">Username *</label>
                                <div className="auth-modal-input-wrap">
                                    <span className="auth-modal-input-icon"><FaUser /></span>
                                    <input
                                        id="reg-username" name="username" type="text"
                                        placeholder="Username"
                                        value={form.username} onChange={handleChange} required
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Email */}
                        <div className="auth-modal-input-group">
                            <label htmlFor="reg-email">Email Address *</label>
                            <div className="auth-modal-input-wrap">
                                <span className="auth-modal-input-icon"><FaEnvelope /></span>
                                <input
                                    id="reg-email" name="email" type="email"
                                    placeholder="name@example.com"
                                    value={form.email} onChange={handleChange} required
                                />
                            </div>
                        </div>

                        {/* Passwords */}
                        <div className="auth-modal-grid-row">
                            <div className="auth-modal-input-group">
                                <label htmlFor="reg-password">Password *</label>
                                <div className="auth-modal-input-wrap">
                                    <span className="auth-modal-input-icon"><FaLock /></span>
                                    <input
                                        id="reg-password" name="password"
                                        type={showPassword ? "text" : "password"}
                                        placeholder="Min 6 characters"
                                        value={form.password} onChange={handleChange} required
                                    />
                                    <span className="auth-modal-eye" onClick={() => setShowPassword(!showPassword)}>
                                        {showPassword ? <FaEyeSlash /> : <FaEye />}
                                    </span>
                                </div>
                            </div>
                            <div className="auth-modal-input-group">
                                <label htmlFor="reg-confirm">Confirm Password *</label>
                                <div className="auth-modal-input-wrap">
                                    <span className="auth-modal-input-icon"><FaLock /></span>
                                    <input
                                        id="reg-confirm" name="confirmPassword"
                                        type={showConfirm ? "text" : "password"}
                                        placeholder="Repeat password"
                                        value={form.confirmPassword} onChange={handleChange} required
                                    />
                                    <span className="auth-modal-eye" onClick={() => setShowConfirm(!showConfirm)}>
                                        {showConfirm ? <FaEyeSlash /> : <FaEye />}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Mobile & City */}
                        <div className="auth-modal-grid-row">
                            <div className="auth-modal-input-group">
                                <label htmlFor="reg-mobile">Mobile Number *</label>
                                <div className="auth-modal-input-wrap">
                                    <span className="auth-modal-input-icon"><FaPhone /></span>
                                    <input
                                        id="reg-mobile" name="mobile" type="tel"
                                        placeholder="10-digit mobile"
                                        value={form.mobile} onChange={handleChange}
                                        required
                                    />
                                </div>
                            </div>
                            <div className="auth-modal-input-group">
                                <label htmlFor="reg-city">City</label>
                                <div className="auth-modal-input-wrap">
                                    <span className="auth-modal-input-icon"><FaCity /></span>
                                    <input
                                        id="reg-city" name="city" type="text"
                                        placeholder="City"
                                        value={form.city} onChange={handleChange}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Address & Pincode */}
                        <div className="auth-modal-grid-row">
                            <div className="auth-modal-input-group">
                                <label htmlFor="reg-address">Address</label>
                                <div className="auth-modal-input-wrap">
                                    <span className="auth-modal-input-icon"><FaMapMarkerAlt /></span>
                                    <input
                                        id="reg-address" name="address" type="text"
                                        placeholder="Street address"
                                        value={form.address} onChange={handleChange}
                                    />
                                </div>
                            </div>
                            <div className="auth-modal-input-group">
                                <label htmlFor="reg-pincode">Pincode</label>
                                <div className="auth-modal-input-wrap">
                                    <span className="auth-modal-input-icon"><FaHashtag /></span>
                                    <input
                                        id="reg-pincode" name="pincode" type="text"
                                        placeholder="6-digit pincode"
                                        value={form.pincode} onChange={handleChange}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Terms & Conditions Checkbox — required before registration */}
                        <div style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "0.83rem", color: "#475569", margin: "12px 0 4px" }}>
                            <input
                                type="checkbox"
                                id="reg-modal-terms"
                                checked={agreeTerms}
                                onChange={(e) => setAgreeTerms(e.target.checked)}
                                style={{ width: "16px", height: "16px", cursor: "pointer", accentColor: "#0B72B9", flexShrink: 0 }}
                            />
                            <label htmlFor="reg-modal-terms" style={{ cursor: "pointer", userSelect: "none", lineHeight: "1.4" }}>
                                I agree to the{" "}
                                <a href="/terms-and-conditions" target="_blank" rel="noopener noreferrer" style={{ color: "#0B72B9", fontWeight: "700", textDecoration: "underline" }}>
                                    Terms &amp; Conditions
                                </a>
                            </label>
                        </div>

                        <button type="submit" className="auth-modal-btn" disabled={loading || !agreeTerms}>
                            {loading ? "Registering..." : <><FaUserPlus /> Register Account</>}
                        </button>
                    </form>
                </div>
            </div>

            <ToastContainer
                position="top-right"
                autoClose={3000}
                closeOnClick draggable pauseOnHover
                theme="colored"
                style={{ zIndex: 99999 }}
            />
        </div>
    );
}

export default RegisterModal;
